/**
 * Shared utility functions for normalizing category_order across services and custom codes.
 */

export const toSlug = (value) => {
  const stringValue = value == null ? '' : String(value);
  const slug = stringValue.trim().replaceAll(/\s+/g, '-').toLowerCase();
  return slug || '';
};

/**
 * Normalize category_order for state updates (not for payload building).
 * Rules:
 * - Has category + has category_order → keep existing order as-is
 * - Has category + no category_order  → assign a fresh unique order at end
 * - No category + has category_order  → remove category_order (invalid)
 * - No category + no category_order   → leave as-is
 *
 * Two rows with the same category name but DIFFERENT category_order values are
 * intentionally kept as separate groups — they are NOT merged. This allows the
 * user to type a category name that matches an existing category without having
 * their row silently merged into that group mid-keystroke.
 */
const collectExistingOrders = (services) => {
  const existing = new Set();
  for (const s of services) {
    if (s.category?.trim() && s.category_order != null) {
      existing.add(s.category_order);
    }
  }
  return existing;
};

const assignMissingOrder = (service, nextOrderRef) => {
  service.category_order = nextOrderRef.value++;
};

/**
 * Normalize category_order for state updates (not for payload building).
 * Rules:
 * - Has category + has category_order → keep existing order as-is
 * - Has category + no category_order  → assign a fresh unique order at end
 * - No category + has category_order  → remove category_order (invalid)
 * - No category + no category_order   → leave as-is
 *
 * Two rows with the same category name but DIFFERENT category_order values are
 * intentionally kept as separate groups — they are NOT merged.
 */
export const normalizeCategoryOrderInServices = (services = []) => {
  const cloned = services.map((s) => ({ ...s }));
  const existingOrders = collectExistingOrders(cloned);
  const nextOrderRef = {
    value: existingOrders.size > 0 ? Math.max(...existingOrders) + 1 : 1,
  };

  for (const service of cloned) {
    const hasCategory = Boolean(service.category?.trim());
    const hasOrder = service.category_order != null;

    if (!hasCategory) {
      delete service.category_order;
    } else if (!hasOrder) {
      assignMissingOrder(service, nextOrderRef);
    }
    // hasCategory && hasOrder → keep as-is
  }

  return cloned;
};

/**
 * Normalize category_order for state updates (not for payload building).
 * Rules:
 * - Category with category_order → keep it
 * - Category without category_order → assign at end
 */
export const normalizeCategoryOrderInCustomCodes = (custom = []) => {
  const existingOrders = custom
    .map((c) => c.category_order ?? c.codes?.[0]?.category_order)
    .filter((o) => o != null);

  let nextOrder = existingOrders.length > 0 ? Math.max(...existingOrders) + 1 : 1;

  return custom.map((category, index) => {
    const category_order =
      category.category_order ?? category.codes?.[0]?.category_order ?? nextOrder++;

    const id = category.id || `cat-${index + 1}-${toSlug(category.name || '')}`;

    return {
      ...category,
      id,
      category_order,
      codes: (category.codes ?? []).map((code) => ({ ...code, category_order })),
    };
  });
};

/**
 * Transform API response from loop/plans/custom-codes/process-template (flat array per row)
 * into nested customCodes shape: [{ id, name, category_order, codes: [...] }].
 */
export const transformImportedCustomCodes = (apiRows = []) => {
  if (!Array.isArray(apiRows) || apiRows.length === 0) return [];
  const byCategory = new Map();
  let categoryOrder = 0;
  apiRows.forEach((row, index) => {
    const catName = row.category == null ? '' : String(row.category).trim();
    if (!catName) return;
    if (!byCategory.has(catName)) {
      categoryOrder += 1;
      byCategory.set(catName, {
        id: `cat_${toSlug(catName)}_${index}`,
        name: catName,
        category_order: categoryOrder,
        codes: [],
      });
    }
    const cat = byCategory.get(catName);
    cat.codes.push({
      id: `code_${toSlug(row.code || '')}_${index}`,
      code: row.code ?? '',
      description: row.name ?? '',
      discount_type: row.discount_type || 'discount_percent',
      discount_value: row.discount_value ?? 0,
      display_type: row.display_type || 'fees_list',
      category_order: cat.category_order,
    });
  });
  return Array.from(byCategory.values());
};

/**
 * Build the final services + customCodes payload for the backend.
 *
 * This is called at save time. It derives category_order purely from the
 * current visual order of the arrays (array position = what the user sees),
 * ignoring any stale stored values. It also merges the two separate tables
 * (services without category, custom codes with category) into one flat list
 * and re-sequences category_order so there are no collisions.
 *
 * Rules applied here:
 * 1. services without category  → no category_order
 * 2. services with category     → category_order from their visual position
 * 3. customCodes                → category_order from their array index (visual position)
 * 4. After merging both tables, re-sequence so orders are 1,2,3… with no gaps/duplicates
 */
export const buildServicesPayload = (serviceList = [], customCodes = []) => {
  // --- services table (no category) ---
  const plainServices = serviceList
    .filter((s) => !s.category?.trim())
    .map(({ order, display_type, enableLifeTimeValue, retail_price, actions,
             originalIndex, orderNumber, categoryName, categoryOrder, category_order, ...rest }) => ({
      code: rest.code || '',
      name: rest.name || '',
      discount_type: rest.discount_type || 'discount_percent',
      discount_value: rest.discount_value || 0,
    }));

  // --- services table (with category) – visual order = array position ---
  const serviceCategoryOrderMap = new Map();
  let nextServiceOrder = 1;
  for (const s of serviceList) {
    const key = s.category?.trim();
    if (key && !serviceCategoryOrderMap.has(key)) {
      serviceCategoryOrderMap.set(key, nextServiceOrder++);
    }
  }

  const categorisedServices = serviceList
    .filter((s) => s.category?.trim())
    .map(({ order, display_type, enableLifeTimeValue, retail_price, actions,
             originalIndex, orderNumber, categoryName, categoryOrder, ...rest }) => ({
      category: rest.category,
      category_order: serviceCategoryOrderMap.get(rest.category?.trim()),
      code: rest.code || '',
      name: rest.name || '',
      discount_type: rest.discount_type || 'discount_percent',
      discount_value: rest.discount_value || 0,
    }));

  // --- custom codes table – visual order = array index ---
  const customAsServices = customCodes.flatMap((category, index) => {
    const category_order = index + 1;
    return (category.codes || []).map((code) => ({
      category: category.name || '',
      category_order,
      code: code.code || '',
      name: code.description || '',
      discount_type: code.discount_type || 'discount_percent',
      discount_value: code.discount_value || 0,
      display_type: code.display_type || 'fees_list',
    }));
  });

  // --- merge and re-sequence ---
  // Both tables may have overlapping category names with different orders.
  // Walk through in order: categorisedServices first, then customAsServices,
  // and assign a fresh sequential order per unique category name.
  const mergedWithCategory = [...categorisedServices, ...customAsServices];
  const mergedCategoryOrderMap = new Map();
  let nextMergedOrder = 1;
  for (const s of mergedWithCategory) {
    const key = s.category?.trim();
    if (key && !mergedCategoryOrderMap.has(key)) {
      mergedCategoryOrderMap.set(key, nextMergedOrder++);
    }
  }

  const resequenced = mergedWithCategory.map((s) => ({
    ...s,
    category_order: mergedCategoryOrderMap.get(s.category?.trim()),
  }));

  return [...plainServices, ...resequenced];
};
